import { useEffect, useState, useMemo, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, Trash2, Mail, Tag, User, MessageSquare } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const STATUSES = ["new", "contacted", "converted", "closed"];

export default function WaitlistTab() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/waitlist");
      setEntries(data);
    } catch {
      toast.error("Failed to load waitlist.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/waitlist/${id}`, { status });
      setEntries((prev) => prev.map((e) => (e.id === id ? data : e)));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed.");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/waitlist/${id}`);
      setEntries((prev) => prev.filter((e) => e.id !== id));
      toast.success("Removed.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const stats = useMemo(() => ({
    total: entries.length,
    merch: entries.filter((e) => (e.source || "").startsWith("Merch")).length,
    programs: entries.filter((e) => (e.source || "") === "waitlist").length,
  }), [entries]);

  return (
    <div data-testid="waitlist-tab">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl">Waitlist</h1>
        <button
          data-testid="waitlist-refresh"
          onClick={load}
          className="inline-flex items-center gap-2 border border-white/20 px-4 py-2 text-xs uppercase tracking-widest font-bold hover:bg-white hover:text-black transition-colors"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-3 gap-px bg-white/10 border border-white/10 mb-10">
        {[
          { label: "Total", value: stats.total },
          { label: "Programs / Membership", value: stats.programs },
          { label: "Merch Interest", value: stats.merch },
        ].map((s) => (
          <div key={s.label} className="bg-[#0A0A0A] p-6" data-testid={`waitlist-stat-${s.label.toLowerCase().split(" ")[0]}`}>
            <p className="font-mono-accent text-4xl font-bold">{s.value}</p>
            <p className="text-white/40 text-xs uppercase tracking-wide mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-white/40"><Loader2 className="animate-spin mr-2" /> Loading...</div>
      ) : entries.length === 0 ? (
        <div className="text-center py-24 text-white/40 border border-white/10 bg-[#121212]" data-testid="waitlist-empty">No waitlist signups yet.</div>
      ) : (
        <div className="space-y-3" data-testid="waitlist-list">
          {entries.map((e) => (
            <div key={e.id} data-testid={`waitlist-${e.id}`} className="bg-[#121212] border border-white/10 hover:border-white/25 transition-colors p-5 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="font-display font-bold uppercase tracking-tight text-xl">{e.name}</h3>
                  {e.role && <span className="font-mono-accent text-[10px] tracking-[0.2em] uppercase border border-white/25 text-white/50 px-2 py-0.5">{e.role}</span>}
                  {e.source && e.source !== "waitlist" && <span className="font-mono-accent text-[10px] tracking-[0.2em] uppercase border border-white/40 text-white/70 px-2 py-0.5 flex items-center gap-1"><Tag size={10} /> {e.source}</span>}
                </div>
                <div className="flex gap-x-6 gap-y-1 flex-wrap text-sm text-white/60 mt-2">
                  <span className="flex items-center gap-2"><Mail size={14} className="text-white/60" /> {e.email}</span>
                  {e.sport && <span className="flex items-center gap-2"><User size={14} className="text-white/60" /> {e.sport}</span>}
                </div>
                {e.notes && <p className="mt-2 text-sm text-white/60 flex items-start gap-2"><MessageSquare size={14} className="text-white/40 mt-0.5" /> {e.notes}</p>}
                <p className="mt-2 font-mono-accent text-[10px] tracking-wider uppercase text-white/30">{new Date(e.created_at).toLocaleString()}</p>
              </div>
              <div className="flex items-center gap-3">
                <select
                  data-testid={`waitlist-status-${e.id}`}
                  value={STATUSES.includes(e.status) ? e.status : "new"}
                  onChange={(ev) => setStatus(e.id, ev.target.value)}
                  className="bg-[#0A0A0A] border border-white/15 text-xs uppercase tracking-widest px-3 py-2 outline-none focus:border-white"
                >
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <button data-testid={`waitlist-delete-${e.id}`} onClick={() => remove(e.id)} className="text-white/30 hover:text-red-400 transition-colors"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
